import { find } from 'lodash';
import { v5 as uuidV5 } from 'uuid';
import { Book, Protocol, ProtocolContentType, ProtocolType, RecordFile, Signature, } from "../../entities";
import { isRecord, isReplaceChange } from "../types";
import logger from "../../logger";
import { FIXED_PROTOCOL_CONTENT_TYPE_IDS, FIXED_PROTOCOL_TYPE_IDS, getEquipmentItemSchema, getRepeatedSchema, } from "../../utils";
import GatewayParsingError from './GatewayParsingError';
import { getAuthorRefForGateway, getAuthorRefFromGateway } from './ParsingUtils';
const FALLBACK_TIMESTAMP_ENTRY_TYPE = '__FALLBACK_TIMESTAMP__';
const DEFAULT_ENTRY_SOURCE = 'anschuetz-elog-webapp';
export const POSITION_ENTRY_TYPE = 'pos';
export const SOG_COG_ENTRY_TYPE = 'sog_cog';
export const ENCRYPTED_DATA_ENTRY_TYPE = 'encryptedData';
export const ENCRYPTION_INFO_ENTRY_TYPE = 'encryptionInfo';
export const REPORT_TYPE_EXTENSION = 'ReportType';
export const RECORD_TYPE_EXTENSION = 'RecordType';
export const FILE_ATTACHMENTS_EXTENSION = 'FileAttachments';
export const CONFIDENTIAL_EXTENSION = 'Confidential';
const REPORT_TYPE_NAME_MAP = {
    [FIXED_PROTOCOL_TYPE_IDS.NOON_REPORT]: 'noonReport',
};
export default class GatewayRecordParser {
    app;
    constructor(app) {
        this.app = app;
    }
    get localCache() {
        return this.app.get('localCache');
    }
    handlesProtocol(protocol) {
        if (!('_id' in protocol.type)) {
            return false;
        }
        if (protocol.type._id === FIXED_PROTOCOL_TYPE_IDS.GENERIC) {
            return false;
        }
        try {
            this.localCache.getProtocolType(protocol.type._id);
            return true;
        }
        catch (error) {
            return false;
        }
    }
    getExtensions(protocol) {
        const extensions = [
            this.getRecordTypeExtension(protocol),
            this.getReportTypeExtension(protocol),
            this.getFileAttachmentExtension(protocol),
        ];
        const confidentialExtension = this.getConfidentialExtension(protocol);
        if (confidentialExtension) {
            extensions.push(confidentialExtension);
        }
        return extensions;
    }
    getTypeId(protocol) {
        if ('_id' in protocol.type) {
            return protocol.type._id;
        }
        throw new Error(`Parser does not support protocols of type ${protocol.type.type}`);
    }
    getRecordTypeExtension(protocol) {
        return {
            Manufacturer: 'ANZ',
            Name: RECORD_TYPE_EXTENSION,
            Value: this.getTypeId(protocol),
        };
    }
    getReportTypeExtension(protocol) {
        const protocolType = this.localCache.getOptionalProtocolType(this.getTypeId(protocol));
        return {
            Manufacturer: 'ANZ',
            Name: REPORT_TYPE_EXTENSION,
            Value: protocolType?.isReport
                ?
                    REPORT_TYPE_NAME_MAP[protocolType._id] || protocolType._id
                : protocol.books.map(({ _id: bookId }) => this.localCache.getBookById(bookId).technicalName),
        };
    }
    getFileAttachmentExtension(protocol) {
        return {
            Manufacturer: 'ANZ',
            Name: FILE_ATTACHMENTS_EXTENSION,
            Value: protocol.files.map(({ _id: fileId }) => fileId),
        };
    }
    getConfidentialExtension(protocol) {
        const confidential = protocol.books.some(({ _id: bookId }) => this.localCache.getBookById(bookId).confidential);
        if (confidential && protocol.books.length > 1) {
            throw new GatewayParsingError('Invalid combination of confidential book assignments');
        }
        if (!confidential) {
            return undefined;
        }
        return {
            Manufacturer: 'ANZ',
            Name: CONFIDENTIAL_EXTENSION,
            Value: confidential,
        };
    }
    parseProtocol(protocol) {
        const extensions = this.getExtensions(protocol);
        const record = {
            Author: getAuthorRefForGateway(protocol.author, this.app),
            Entries: this.getEntries(protocol),
            Id: protocol._id,
            Extensions: extensions,
            SignedWhen: protocol.createdTimestamp,
        };
        return record;
    }
    getCommonEntry(protocol) {
        return {
            Acquisition: 'man',
            TimeOfData: protocol.timestamp,
            TimeOfRecording: protocol.createdTimestamp,
            Source: protocol.clientDeviceId || DEFAULT_ENTRY_SOURCE,
        };
    }
    generateEntry(id, type, value) {
        return {
            Id: uuidV5(type, id),
            Type: type,
            Value: value,
        };
    }
    getEntries(protocol) {
        const commonEntry = this.getCommonEntry(protocol);
        const positionEntry = {
            ...commonEntry,
            Id: uuidV5(POSITION_ENTRY_TYPE, protocol._id),
            Type: POSITION_ENTRY_TYPE,
            Value: protocol.position,
        };
        const sogCogEntry = {
            ...commonEntry,
            Id: uuidV5(SOG_COG_ENTRY_TYPE, protocol._id),
            Type: SOG_COG_ENTRY_TYPE,
            Value: protocol.sogCog,
        };
        const encryptedContent = protocol.contents.find((content) => content.type._id === FIXED_PROTOCOL_CONTENT_TYPE_IDS.ENCRYPTED);
        if (encryptedContent) {
            return [
                positionEntry,
                sogCogEntry,
                {
                    Id: uuidV5(ENCRYPTED_DATA_ENTRY_TYPE, protocol._id),
                    Type: ENCRYPTED_DATA_ENTRY_TYPE,
                    Value: encryptedContent.data.encryptedData,
                },
                {
                    Id: uuidV5(ENCRYPTION_INFO_ENTRY_TYPE, protocol._id),
                    Type: ENCRYPTION_INFO_ENTRY_TYPE,
                    Value: encryptedContent.data.encryptionInfo,
                },
            ].map((entry) => {
                return {
                    ...entry,
                    ...commonEntry,
                };
            });
        }
        const contentEntries = this.createEntries(protocol);
        if (contentEntries.some((entry) => entry.Type === POSITION_ENTRY_TYPE)) {
            throw new GatewayParsingError(`Entry type '${POSITION_ENTRY_TYPE}' is a reserved type. You must not use it!`);
        }
        if (contentEntries.some((entry) => entry.Type === SOG_COG_ENTRY_TYPE)) {
            throw new GatewayParsingError(`Entry type '${SOG_COG_ENTRY_TYPE}' is a reserved type. You must not use it!`);
        }
        const entries = [positionEntry, sogCogEntry, ...contentEntries].filter((entry) => entry.Value === 0 || entry.Value || entry.Unknown);
        if (!entries.length) {
            return [
                {
                    ...commonEntry,
                    Id: uuidV5(FALLBACK_TIMESTAMP_ENTRY_TYPE, protocol._id),
                    Type: FALLBACK_TIMESTAMP_ENTRY_TYPE,
                    Value: '',
                },
            ];
        }
        return entries.map((entry) => {
            return {
                ...entry,
                ...commonEntry,
            };
        });
    }
    getEntryId(element, protocolId) {
        if (element.type === undefined) {
            throw new Error('Invalid element');
        }
        return uuidV5(element.name, protocolId);
    }
    createSchemaEntries(protocol, content, schema) {
        if (!content) {
            return [];
        }
        return schema
            .map((element) => {
            if (element.type === undefined) {
                return null;
            }
            const entry = {
                Id: this.getEntryId(element, protocol._id),
                Type: element.name,
                Value: content.data[element.name],
            };
            if (entry.Value === undefined || (Array.isArray(entry.Value) && entry.Value.length === 0)) {
                return null;
            }
            return entry;
        })
            .reduce((entries, entry) => {
            if (entry === null) {
                return entries;
            }
            return [...entries, entry];
        }, []);
    }
    createContentTypeEntries(protocol, { ref: contentType, equipmentItem: equipmentItemConfig }) {
        const protocolContentType = this.localCache.getProtocolContentType(contentType._id);
        let schema = protocolContentType.schema;
        if (equipmentItemConfig) {
            const equipmentContentType = this.localCache.getProtocolContentType(equipmentItemConfig.contentType._id);
            const equipmentItemElement = equipmentContentType.schema.find((element) => element.type === 'equipmentitem' && element._id === equipmentItemConfig.elementId);
            const equipmentItemContent = protocol.contents.find((content) => content.type._id === equipmentContentType._id);
            if (!equipmentItemContent || !equipmentItemElement) {
                return [];
            }
            const equipmentItemId = equipmentItemContent.data[equipmentItemElement.name];
            if (typeof equipmentItemId !== 'string') {
                return [];
            }
            schema = this.getEquipmentItemSchema(equipmentItemId, protocolContentType);
            const equipmentItem = this.localCache.getEquipmentItem(equipmentItemId);
            const equipmentList = this.localCache.getEquipmentList(equipmentItem.list._id);
            const itemElementIdForCount = equipmentList.protocolContentTypes.find((config) => config.ref._id === protocolContentType._id)?.itemElementIdForCount;
            if (itemElementIdForCount) {
                const itemElementForCount = equipmentList.itemSchema.find((itemElement) => itemElement.type !== undefined && itemElement._id === itemElementIdForCount);
                const count = itemElementForCount ? equipmentItem.itemData[itemElementForCount.name] : 1;
                if (count !== 1) {
                    schema = getRepeatedSchema(schema, count).reduce((wholeSchema, repeatedSchema) => [...wholeSchema, ...repeatedSchema], []);
                }
            }
        }
        const content = protocol.contents.find((content) => content.type._id === contentType._id);
        return this.createSchemaEntries(protocol, content, schema);
    }
    getEquipmentItemSchema(equipmentItemId, protocolContentType) {
        const equipmentItem = this.localCache.getEquipmentItem(equipmentItemId);
        const equipmentList = this.localCache.getEquipmentList(equipmentItem.list._id);
        return getEquipmentItemSchema(equipmentItem, equipmentList, protocolContentType);
    }
    createEntries(protocol) {
        const protocolType = this.localCache.getProtocolType(this.getTypeId(protocol));
        const fields = protocolType.contentTypes
            .map((contentType) => {
            return this.createContentTypeEntries(protocol, contentType);
        })
            .reduce((entries, contentEntries) => [...entries, ...contentEntries], []);
        return fields;
    }
    getProtocolType(record) {
        let extensions = [];
        if (isRecord(record)) {
            extensions = record.Extensions || [];
        }
        else if (isReplaceChange(record.Changes[0])) {
            extensions = record.Changes[0].ReplacementRecord.Extensions || [];
        }
        else {
            extensions = record.Changes[0].Extensions || [];
        }
        const recordTypeExtension = extensions.find((extension) => extension.Manufacturer === 'ANZ' && extension.Name === RECORD_TYPE_EXTENSION);
        if (!recordTypeExtension) {
            throw new Error(`Protocol type for record can not be determined because extension missing.`);
        }
        return this.localCache.getProtocolType(recordTypeExtension.Value);
    }
    getTypeRefForProtocol(record) {
        return ProtocolType.createRef(this.getProtocolType(record)._id);
    }
    handlesRecord(record) {
        try {
            this.getProtocolType(record);
            return true;
        }
        catch (error) {
            return false;
        }
    }
    parseSchemaData(record, schema) {
        return schema
            .map((element) => {
            if (element.type === undefined) {
                return {};
            }
            const entry = this.getEntry(element, record);
            if (entry === undefined) {
                return {};
            }
            return { [element.name]: entry.Value };
        })
            .reduce((contentData, elementData) => {
            return { ...contentData, ...elementData };
        }, {});
    }
    parseContentTypeContent(record, { ref: contentType, equipmentItem: equipmentItemConfig }) {
        const protocolContentType = this.localCache.getProtocolContentType(contentType._id);
        let schema = protocolContentType.schema;
        if (equipmentItemConfig) {
            const equipmentContentType = this.localCache.getProtocolContentType(equipmentItemConfig.contentType._id);
            const equipmentItemElement = equipmentContentType.schema.find((element) => element.type === 'equipmentitem' && element._id === equipmentItemConfig.elementId);
            if (!equipmentItemElement) {
                schema = [];
            }
            else {
                const equipmentItemId = this.getEntry(equipmentItemElement, record)?.Value;
                if (typeof equipmentItemId === 'string') {
                    schema = this.getEquipmentItemSchema(equipmentItemId, protocolContentType);
                    const equipmentItem = this.localCache.getEquipmentItem(equipmentItemId);
                    const equipmentList = this.localCache.getEquipmentList(equipmentItem.list._id);
                    const itemElementIdForCount = equipmentList.protocolContentTypes.find((config) => config.ref._id === protocolContentType._id)?.itemElementIdForCount;
                    if (itemElementIdForCount) {
                        const itemElementForCount = equipmentList.itemSchema.find((itemElement) => itemElement.type !== undefined && itemElement._id === itemElementIdForCount);
                        const count = itemElementForCount ? equipmentItem.itemData[itemElementForCount.name] : 1;
                        if (count !== 1) {
                            schema = getRepeatedSchema(schema, count).reduce((wholeSchema, repeatedSchema) => [...wholeSchema, ...repeatedSchema], []);
                        }
                    }
                }
                else {
                    schema = [];
                }
            }
        }
        return [
            {
                type: ProtocolContentType.createRef(contentType._id),
                data: this.parseSchemaData(record, schema),
            },
        ];
    }
    parseEncryptedContent(record) {
        const encryptedData = record.Entries.find((entry) => entry.Type === ENCRYPTED_DATA_ENTRY_TYPE)?.Value;
        const encryptionInfo = record.Entries.find((entry) => entry.Type === ENCRYPTION_INFO_ENTRY_TYPE)?.Value;
        if (encryptedData && encryptionInfo) {
            return {
                type: ProtocolContentType.createRef(FIXED_PROTOCOL_CONTENT_TYPE_IDS.ENCRYPTED),
                data: {
                    encryptedData,
                    encryptionInfo,
                },
            };
        }
        return undefined;
    }
    parseProtocolContent(record) {
        const encryptedContent = this.parseEncryptedContent(record);
        if (encryptedContent) {
            return [encryptedContent];
        }
        const protocolType = this.getProtocolType(record);
        return protocolType.contentTypes
            .map((contentType) => this.parseContentTypeContent(record, contentType))
            .filter((content) => content !== null)
            .reduce((all, contentTypes) => [...all, ...contentTypes], []);
    }
    createProtocol(timestamp, author, type, contents, books, position, sogCog) {
        return new Protocol({ timestamp, author, type, contents, books, position, sogCog });
    }
    getEntry(element, record) {
        return record.Entries.find((entry) => entry.Type === element.name);
    }
    parseRecord(record, timestamp, signature) {
        const protocol = this.createProtocol(timestamp, getAuthorRefFromGateway(record.Author, this.app), this.getTypeRefForProtocol(record), this.parseProtocolContent(record), this.getBooks(record), this.getPosition(record), this.getSogCog(record));
        protocol.files = this.getFiles(record);
        protocol.clientDeviceId = this.getSource(record);
        if (signature &&
            record.SignInfo &&
            record.SignInfo.SignatureMethod &&
            record.SignInfo.KeyInfo &&
            record.SignInfo.KeyInfo.KeyName) {
            protocol.signature = new Signature(signature, record.SignInfo.KeyInfo.KeyName, record.SignInfo.SignatureMethod);
        }
        Object.assign(protocol, {
            _id: record.Id,
            createdTimestamp: record.SignedWhen || timestamp,
            gatewayAmendmentId: record.Id,
        });
        return protocol;
    }
    getFiles(record) {
        let extensions = [];
        if (isRecord(record)) {
            extensions = record.Extensions || [];
        }
        else {
            extensions = record.Changes[0].Extensions || [];
        }
        const fileAttachmentExtension = find(extensions, { Name: FILE_ATTACHMENTS_EXTENSION });
        if (!fileAttachmentExtension) {
            return [];
        }
        const files = fileAttachmentExtension.Value.map((fileId) => RecordFile.createRef(fileId));
        return files;
    }
    getBooks(record) {
        let extensions = [];
        if (isRecord(record)) {
            extensions = record.Extensions || [];
        }
        else {
            extensions = record.Changes[0].Extensions || [];
        }
        const reportTypeExtension = find(extensions, { Name: REPORT_TYPE_EXTENSION });
        if (!reportTypeExtension) {
            return [];
        }
        let bookNames = [];
        if (typeof reportTypeExtension.Value === 'string') {
            if (reportTypeExtension.Value.includes('[')) {
                bookNames = bookNames.concat(JSON.parse(reportTypeExtension.Value));
            }
            else {
                bookNames.push(reportTypeExtension.Value);
            }
        }
        else if (Array.isArray(reportTypeExtension.Value)) {
            bookNames = reportTypeExtension.Value;
        }
        return bookNames.reduce((books, bookName) => {
            try {
                if (Object.values(REPORT_TYPE_NAME_MAP).includes(bookName) ||
                    this.localCache.getOptionalProtocolType(bookName)?.isReport) {
                    return books;
                }
                const book = this.localCache.getBook(bookName);
                return [...books, Book.createRef(book._id)];
            }
            catch (error) {
                logger.warn(`Error at getting book of record. Book will be ignored. Error: ${error}`);
                return books;
            }
        }, []);
    }
    getSource(record) {
        const sources = new Set(record.Entries.map((entry) => entry.Source));
        if (sources.size === 0) {
            return DEFAULT_ENTRY_SOURCE;
        }
        return [...sources][0];
    }
    getPosition(record) {
        const positionEntry = record.Entries.find((entry) => entry.Type === POSITION_ENTRY_TYPE);
        if (!positionEntry) {
            return undefined;
        }
        return positionEntry.Value;
    }
    getSogCog(record) {
        const sogCogEntry = record.Entries.find((entry) => entry.Type === SOG_COG_ENTRY_TYPE);
        if (!sogCogEntry) {
            return undefined;
        }
        return sogCogEntry.Value;
    }
}
//# sourceMappingURL=GatewayRecordParser.js.map